import { useTranslation } from "react-i18next"
import type { PeerProfile } from "../../types"
import { PeerAvatar, PeerAvatarGroup } from "../PeerAvatar"
import s from "./ThreadIntro.module.scss"

type ThreadIntroProps = {
  name: string
  faces: PeerProfile[]
  memberCount?: number
  /** Groups only, and only for those who may add. */
  onAddMembers?: () => void
}

/** Hand-placed, so the ring reads as deliberate rather than random. */
const STARS = [
  { left: "-6%", top: "6%", size: 15, delay: 0, color: "blue" },
  { left: "92%", top: "0%", size: 20, delay: 0.45, color: "yellow" },
  { left: "101%", top: "48%", size: 12, delay: 1.1, color: "blue" },
  { left: "-12%", top: "58%", size: 17, delay: 0.75, color: "pink" },
  { left: "18%", top: "-12%", size: 11, delay: 1.5, color: "yellow" },
  { left: "74%", top: "96%", size: 13, delay: 1.9, color: "blue" },
] as const

/** A conversation with nothing in it yet: who it is with, framed in sparkles. */
export function ThreadIntro({
  name,
  faces,
  memberCount,
  onAddMembers,
}: ThreadIntroProps) {
  const { t } = useTranslation()

  return (
    <div className={s.intro}>
      <div className={s.intro__faces}>
        {faces.length > 1 ? (
          <PeerAvatarGroup members={faces} size="large" />
        ) : (
          faces[0] && <PeerAvatar {...faces[0]} size="large" />
        )}

        {STARS.map((star) => (
          <svg
            key={`${star.left} ${star.top}`}
            className={`${s.intro__star} ${s[`intro__star--${star.color}`]}`}
            style={{
              left: star.left,
              top: star.top,
              animationDelay: `${star.delay}s`,
            }}
            width={star.size}
            height={star.size}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M12 0c.7 5.6 2.4 9.7 6 11.1 1.9.7 3.9.9 6 .9-5.6.7-9.7 2.4-11.1 6-.7 1.9-.9 3.9-.9 6-.7-5.6-2.4-9.7-6-11.1-1.9-.7-3.9-.9-6-.9 5.6-.7 9.7-2.4 11.1-6C11.8 4.1 12 2.1 12 0Z" />
          </svg>
        ))}
      </div>

      <p className={s.intro__name}>{name}</p>
      <p className={s.intro__subtitle}>
        {memberCount !== undefined
          ? t("chat.MEMBER_COUNT", { count: memberCount })
          : t("chat.EMPTY_THREAD_MESSAGES")}
      </p>

      {onAddMembers && (
        <button
          className={s.intro__action}
          type="button"
          onClick={onAddMembers}
        >
          {t("chat.ADD_MEMBERS")}
        </button>
      )}
    </div>
  )
}
